import React, { useReducer } from "react";

import { Button, Label } from "@busbud/horizon";
import { IconBusbudCoin } from "@busbud/horizon";

import {
  getLocalizedPrice,
  LocalizedPrice
} from "@app/components/localized-price";
import { useAppContext } from "@app/helpers/hooks";

import type { ConnectorProps } from "./redeem-credits.connect";

interface Props extends ConnectorProps {}

interface State {
  loading: boolean;
  error: boolean;
}

function getInitialState(): State {
  return {
    loading: false,
    error: false
  };
}

const reducer = (state: State, action: string): State => {
  switch (action) {
    case "start_loading":
      return { ...state, loading: true, error: false };
    case "end_loading":
      return { ...state, loading: false, error: false };
    case "error":
      return { ...state, loading: false, error: true };
    default:
      return state;
  }
};

export const RedeemCredits: React.FC<Props> = ({
  balance,
  currency,
  has_redeemed,
  redeemed_amount,
  cart_total,
  redeemCredits,
  unredeemCredits
}) => {
  const context = useAppContext();
  const { api, tracker, translator } = useAppContext();
  const [state, dispatch] = useReducer(reducer, getInitialState());

  const is_redeem_available = balance && balance > 0;
  const amount_to_display =
    balance && balance < cart_total ? balance : cart_total;
  const amount_to_redeem_localized = getLocalizedPrice(
    {
      price_in_cents: amount_to_display,
      currency
    },
    context
  );
  const redeemed_amount_localized = getLocalizedPrice(
    {
      price_in_cents: redeemed_amount,
      currency
    },
    context
  );
  const button_label = has_redeemed
    ? translator.t("!checkout.loyalty.redeem-undo", {
        amount: redeemed_amount_localized
      })
    : translator.t("!checkout.loyalty.redeem-apply", {
        amount: amount_to_redeem_localized
      });

  const onRedeem = async () => {
    dispatch("start_loading");
    try {
      await redeemCredits({ api, tracker });
      dispatch("end_loading");
    } catch (_) {
      dispatch("error");
    }
  };

  const onUnredeem = async () => {
    dispatch("start_loading");
    try {
      await unredeemCredits({ api, tracker });
      dispatch("end_loading");
    } catch (_) {
      dispatch("error");
    }
  };

  if (!is_redeem_available || !currency) {
    return null;
  }

  return (
    <div
      data-testid="redeem-credits-v2"
      className="mx-150 mb-300 flex flex-col rounded-md bg-color-scheme-literal-green-100 px-250 py-200 md:flex-row md:items-center md:justify-between"
    >
      <div className="mb-200 mr-100 flex items-center gap-100 md:mb-0">
        <IconBusbudCoin
          size="lg"
          className="text-color-scheme-literal-green-500"
        />
        <div className="flex flex-col">
          <Label
            size="lg"
            as="span"
            fontWeight="bold"
            className="text-color-scheme-literal-green-600"
          >
            {translator.t("!checkout.loyalty.busbud-credits")}
          </Label>
          <Label
            size="xl"
            as="span"
            fontWeight="extraBold"
            className="text-color-scheme-literal-green-600"
          >
            <LocalizedPrice
              price_in_cents={balance}
              currency={currency}
              without_cents={balance === 0}
            />
          </Label>
        </div>
      </div>
      <Button
        isDisabled={state.loading}
        appearance={has_redeemed ? undefined : "primary"}
        variant={has_redeemed ? "contained" : undefined}
        onClick={has_redeemed ? onUnredeem : onRedeem}
      >
        {button_label}
      </Button>
    </div>
  );
};
